import {useState, useEffect, useCallback, useMemo, useRef, KeyboardEvent} from 'react'
import {cn} from '@/lib/utils'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Checkbox} from '@/components/ui/checkbox'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {DatePicker} from '@/components/shared/DatePicker'
import {LookupCombobox} from '@/components/shared/LookupCombobox'
import {labelOf, type LocalizedTypeValue} from '@/hooks/use-app-config'
import type {
    EndpointContractFilterDescriptor,
    FilterDescriptorView,
    SearchAndFilterContract,
} from '@/types/endpoint-contract'
import {IS_NULL_SENTINEL} from '@/types/endpoint-contract'
import type {AttributeDefinition} from '@/types/attribute-definition'
import type {EntityLink, EntityType} from '@/types/entity-link'
import {EntityType as EntityTypeValues} from '@/types/entity-link'
import {ENTITY_LINK_PART_SEPARATOR} from '@/lib/entity-link-packing'

const KNOWN_ENTITY_TYPES: ReadonlySet<string> = new Set(Object.values(EntityTypeValues))

/**
 * Decode a URL-state value into an {@link EntityLink}. The forward shape is
 * pipe-packed `id|displayName|entityType|subType` (each part URL-encoded so
 * embedded `|` and `,` characters survive the round-trip). Hand-crafted URLs
 * carrying an id-only value still resolve — the chip then shows the raw id
 * until the user re-picks an option.
 */
function toEntityLink(packed: string, descriptor: FilterDescriptorView): EntityLink {
    const declared = descriptor.lookup?.entityType
    const fallbackType: EntityType = declared && KNOWN_ENTITY_TYPES.has(declared)
        ? (declared as EntityType)
        : EntityTypeValues.IDENTITY
    if (!packed.includes(ENTITY_LINK_PART_SEPARATOR)) {
        return {id: packed, displayName: packed, entityType: fallbackType, subType: null, code: null}
    }
    const parts = packed.split(ENTITY_LINK_PART_SEPARATOR).map((p) => decodePart(p))
    const id = parts[0] ?? packed
    const displayName = (parts[1] ?? '').length > 0 ? parts[1] : id
    const declaredType = parts[2] && KNOWN_ENTITY_TYPES.has(parts[2])
        ? (parts[2] as EntityType)
        : fallbackType
    const subType = parts[3] != null && parts[3].length > 0 ? parts[3] : null
    return {id, displayName, entityType: declaredType, subType, code: null}
}

/**
 * Encode an {@link EntityLink} into the pipe-packed URL value. Each part is
 * URL-encoded so commas (the CSV delimiter for multi-select) inside a
 * displayName don't corrupt the wire format.
 */
function fromEntityLink(link: EntityLink): string {
    return [
        encodePart(link.id),
        encodePart(link.displayName),
        link.entityType,
        encodePart(link.subType ?? ''),
    ].join(ENTITY_LINK_PART_SEPARATOR)
}

function encodePart(value: string): string {
    return encodeURIComponent(value)
}

function decodePart(value: string): string {
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

// Static CSS selector and attribute names — never user-visible.
const FOCUS_FIRST_SELECTOR = 'input:not([type="hidden"]), button, [role="radio"], [role="checkbox"]'
const PLACEHOLDER_UUID = 'UUID'
const PLACEHOLDER_TAG_INPUT = 'tag1, tag2'
const BOOLEAN_TRUE = 'true'
const BOOLEAN_FALSE = 'false'

/**
 * Default cap on the option count for the **inline horizontal toggle row**
 * rendering of an ENUM filter (issues 342.26 + 342.27). Lists with at most
 * this many options AND short-enough labels render in a single horizontal
 * row of toggle chips; everything above the cap (or with too-long labels)
 * collapses to the popover-anchored Combobox-with-chips. The
 * AppConfig value (`filterPickerInlineThreshold`) overrides this when set;
 * the constant is the fallback for tests and bare-bones renders.
 */
export const MULTIPICKER_INLINE_DEFAULT = 3

/**
 * Default cap above which the {@link MultiPickerPopover} renders its inner
 * search input (issue 401). Lists at or below this count render the picker
 * without a search box (`count <= threshold` → no search); above it
 * (`count > threshold`) the search input appears so longer lists stay
 * usable. The AppConfig value (`filterPickerSearchThreshold`) overrides
 * this when set; the constant is the fallback for tests and bare-bones
 * renders.
 */
export const MULTIPICKER_SEARCH_DEFAULT = 10

/**
 * Per-label character cap that still fits inline. Above this length the
 * panel switches to the Combobox even for tiny option sets — keeps the
 * inline row from wrapping into a vertical mess.
 */
export const MULTIPICKER_INLINE_MAX_LABEL_LENGTH = 12

export interface FilterPanelProps {
    contract: SearchAndFilterContract
    /** Current URL-backed filter values keyed by contract filter key. */
    values: Readonly<Record<string, string | ReadonlyArray<string>>>
    /** Current URL-backed dynamic attribute values keyed by attribute name. */
    dynamicAttrValues: Readonly<Record<string, string | ReadonlyArray<string>>>
    /**
     * Attribute definitions that should render in the panel — already
     * filtered by the caller to {@link AttributeDefinition.filterable} = true
     * and matching entity type (§4.10.1 AppConfig session lifecycle).
     */
    dynamicAttributeDefs: ReadonlyArray<AttributeDefinition>
    /** When false, admin-only filters are hidden (SEC-05 / FE-40). */
    isAdmin: boolean
    /** Label for the Apply button (already translated). */
    applyLabel: string
    /** Label for Clear All (already translated). */
    clearAllLabel: string
    /** i18n label for "Not set" option. */
    notSetLabel: string
    /** i18n label for "Any" on single-ENUM radios. */
    anyLabel: string
    /** i18n label for "Yes" on BOOLEAN toggles. */
    yesLabel: string
    /** i18n label for "No" on BOOLEAN toggles. */
    noLabel: string
    /** i18n label for "From" in date-range pairs. */
    fromLabel: string
    /** i18n label for "To" in date-range pairs. */
    toLabel: string
    /** i18n label used as the accessible name of the region (visually hidden). */
    title: string
    /**
     * i18n placeholder used inside UUID filter inputs. The real entity-picker
     * combobox is wired per-page during the B-phase migrations — until then
     * callers render a plain UUID input (SEC-06 graceful fallback), and this
     * placeholder tells the user the picker is not yet available.
     */
    uuidPlaceholder?: string
    /**
     * When true, the shared {@link DatePicker} is forced to render its
     * native `<input type="date">` branch regardless of viewport width.
     * Used for < 480 px (§5.8) and for tests that need a deterministic
     * input shape. Defaults to auto-detect based on `window.innerWidth`.
     */
    nativeDatePicker?: boolean
    /**
     * Type-table map from `AppConfig.typeValuesByName` — used to resolve
     * raw enum codes (`PERSON`) to localized labels (`Person`) for
     * display. When a descriptor carries a `typeTable` hint that maps
     * into this record, panel options render localized labels while the
     * URL and `value` attribute stay on the raw code.
     */
    typeValuesByName?: Readonly<Record<string, ReadonlyArray<LocalizedTypeValue>>>
    /**
     * Threshold above which ENUM-style multi-select pickers render as a
     * popover-anchored Combobox-with-chips instead of an inline checkbox
     * list. Sourced from `AppConfig.filterPickerInlineThreshold`. Defaults
     * to {@link MULTIPICKER_INLINE_DEFAULT} when the AppConfig value is
     * unavailable. Issue 342.15.
     */
    multiPickerInlineThreshold?: number
    /**
     * Threshold above which the {@link MultiPickerPopover} renders its
     * inner search input. Lists at or below this count render the picker
     * without a search box; above it the search input appears. Sourced
     * from `AppConfig.filterPickerSearchThreshold`. Defaults to
     * {@link MULTIPICKER_SEARCH_DEFAULT} when the AppConfig value is
     * unavailable. Issue 401.
     */
    filterPickerSearchThreshold?: number
    /**
     * Contract-key of the discriminator filter (e.g. `'PARTY_TYPE'` for
     * Parties). When present, dynamic-attribute defs whose `typeCodes` do
     * not overlap the panel's draft selection of this filter are hidden.
     * Universal defs (typeCodes empty) are always shown. Issue 342.19 —
     * scoping reads from `draft.filters` so it updates live as the user
     * checks/unchecks discriminator boxes inside the panel, not only on Apply.
     */
    discriminatorFilterKey?: string
    /**
     * Type codes that pin the discriminator regardless of the panel's draft.
     * When the parent page enforces a locked type (issue 301 by-type routes,
     * issue 680), the discriminator filter is omitted from the contract — but
     * the dynamic-attribute pool still needs to be narrowed by that type, so
     * the page passes its locked value(s) here. Falls through to draft-based
     * narrowing when undefined.
     */
    lockedDiscriminatorValues?: readonly string[]
    onApply(next: {
        filters: Record<string, string | string[]>
        dynamicAttributes: Record<string, string | string[]>
    }): void
    onClearAll(): void
    onClose(): void
    /** WHY: SR users get no audible feedback when toggling a chip value — the
        visual border/background flip is silent. Wiring this callback lets the
        caller emit a polite aria-live announcement naming the section + value
        + new state, matching the visual cue sighted users already get.
        Issue 805. */
    onValueToggle?(params: {section: string; value: string; selected: boolean}): void
}

interface PanelDraft {
    filters: Record<string, string | string[]>
    dynamicAttributes: Record<string, string | string[]>
}

function toInitialDraft(
    values: Readonly<Record<string, string | ReadonlyArray<string>>>,
    dyn: Readonly<Record<string, string | ReadonlyArray<string>>>,
): PanelDraft {
    const out: PanelDraft = {filters: {}, dynamicAttributes: {}}
    for (const [k, v] of Object.entries(values)) {
        out.filters[k] = Array.isArray(v) ? [...(v as ReadonlyArray<string>)] : (v as string)
    }
    for (const [k, v] of Object.entries(dyn)) {
        out.dynamicAttributes[k] = Array.isArray(v) ? [...(v as ReadonlyArray<string>)] : (v as string)
    }
    return out
}

/**
 * Renders one section per contract filter plus one section per dynamic
 * attribute definition. Local draft state commits to the URL only on Apply.
 *
 * Panel is rendered **inline below the bar** (§3.5 inline-collapse variant) —
 * not an overlay. Tab moves naturally out of the region; there is no focus
 * trap. Escape closes. The panel has no internal scroll — when it grows tall,
 * the page scrolls.
 */
export function FilterPanel({
    contract,
    values,
    dynamicAttrValues,
    dynamicAttributeDefs,
    isAdmin,
    applyLabel,
    clearAllLabel,
    notSetLabel,
    anyLabel,
    yesLabel,
    noLabel,
    fromLabel,
    toLabel,
    title,
    uuidPlaceholder,
    nativeDatePicker,
    typeValuesByName,
    multiPickerInlineThreshold = MULTIPICKER_INLINE_DEFAULT,
    filterPickerSearchThreshold = MULTIPICKER_SEARCH_DEFAULT,
    discriminatorFilterKey,
    lockedDiscriminatorValues,
    onApply,
    onClearAll,
    onClose,
    onValueToggle,
}: FilterPanelProps) {
    const [draft, setDraft] = useState<PanelDraft>(() => toInitialDraft(values, dynamicAttrValues))
    const containerRef = useRef<HTMLDivElement>(null)

    // Reset draft whenever the external URL values change (e.g., after Apply
    // in a sibling flow or a browser back). Without this the panel would
    // show stale state when re-opened.
    const valuesSig = JSON.stringify(values) + '|' + JSON.stringify(dynamicAttrValues)
    useEffect(() => {
        setDraft(toInitialDraft(values, dynamicAttrValues))
    }, [valuesSig, values, dynamicAttrValues])

    // Focus the first interactive control on mount so keyboard users land on
    // a real control instead of the region wrapper.
    useEffect(() => {
        if (!containerRef.current) return
        const el = containerRef.current.querySelector<HTMLElement>(FOCUS_FIRST_SELECTOR)
        el?.focus()
    }, [])

    // Escape closes the panel; Tab is NOT trapped (panel is a region, not a
    // modal dialog).
    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
        }
    }, [onClose])

    const updateFilter = useCallback((key: string, value: string | string[] | undefined) => {
        setDraft(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                ...(value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
                    ? {[key]: Array.isArray(value) ? [] : ''}
                    : {[key]: value}),
            },
        }))
    }, [])

    const updateDynamicAttr = useCallback((attrName: string, value: string | string[] | undefined) => {
        setDraft(prev => ({
            ...prev,
            dynamicAttributes: {
                ...prev.dynamicAttributes,
                [attrName]: value === undefined ? '' : value,
            },
        }))
    }, [])

    const handleApply = useCallback(() => {
        const cleaned: PanelDraft = {filters: {}, dynamicAttributes: {}}
        for (const [k, v] of Object.entries(draft.filters)) {
            if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) continue
            cleaned.filters[k] = v
        }
        for (const [k, v] of Object.entries(draft.dynamicAttributes)) {
            if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) continue
            cleaned.dynamicAttributes[k] = v
        }
        onApply(cleaned)
    }, [draft, onApply])

    const handleClearAll = useCallback(() => {
        setDraft({filters: {}, dynamicAttributes: {}})
        onClearAll()
    }, [onClearAll])

    // ──────────────────────────────────────────────────────────────────────
    // Date-range pair grouping — two descriptors that share a `dateRange`
    // group key render as a single from/to section. We group and render them
    // once instead of twice per §4.3 dateRange grouping.
    // ──────────────────────────────────────────────────────────────────────
    const searchParamName = contract.search?.param
    const {groupedFilters, singleFilters} = useMemo(() => {
        const visibleEntries: Array<[string, EndpointContractFilterDescriptor]> = []
        const filterMap = contract.filter as Record<string, EndpointContractFilterDescriptor>
        for (const [key, descriptor] of Object.entries(filterMap)) {
            if (descriptor.adminOnly && !isAdmin) continue
            // Spec §4.4 — the search bar at the top of <UnifiedSearchBar>
            // owns the search text. Skip whichever descriptor backs that
            // slot here so the panel doesn't render a duplicate input. We
            // always skip the legacy `q` name (most contracts use it) and
            // also skip the contract-declared search.param when set, which
            // covers contracts hitting a backend with a named search param
            // (e.g. ServiceAccount uses `name`).
            if (descriptor.name === 'q') continue
            if (searchParamName !== undefined && descriptor.name === searchParamName) continue
            visibleEntries.push([key, descriptor])
        }
        // WHY: discriminator (subtype) filter renders first so the user picks
        //      the type before type-scoped attribute fields appear (680.4).
        if (discriminatorFilterKey) {
            visibleEntries.sort(([keyA], [keyB]) => {
                if (keyA === discriminatorFilterKey) return -1
                if (keyB === discriminatorFilterKey) return 1
                return 0
            })
        }
        const groups: Record<string, Array<[string, EndpointContractFilterDescriptor]>> = {}
        const singles: Array<[string, EndpointContractFilterDescriptor]> = []
        for (const entry of visibleEntries) {
            const [, desc] = entry
            if (desc.dateRange) {
                if (!groups[desc.dateRange]) groups[desc.dateRange] = []
                groups[desc.dateRange].push(entry)
            } else {
                singles.push(entry)
            }
        }
        return {groupedFilters: groups, singleFilters: singles}
    }, [contract.filter, isAdmin, discriminatorFilterKey, searchParamName])

    // Issue 342.19 — discriminator scoping reads from `draft.filters` so it
    // updates live as the user toggles checkboxes inside the panel. When no
    // discriminatorFilterKey is supplied, every dynamic attr is offered.
    const scopedDynamicAttributeDefs: ReadonlyArray<AttributeDefinition> = useMemo(() => {
        let selected: ReadonlyArray<string> = []
        if (lockedDiscriminatorValues && lockedDiscriminatorValues.length > 0) {
            selected = lockedDiscriminatorValues
        } else if (discriminatorFilterKey) {
            const rawSelected = draft.filters[discriminatorFilterKey]
            selected = Array.isArray(rawSelected)
                ? (rawSelected as string[])
                : (typeof rawSelected === 'string' && rawSelected.length > 0 ? rawSelected.split(',') : [])
        }
        // WHY: with a discriminator in the contract and nothing selected,
        //      hide type-scoped attrs — only universal + discriminator
        //      render. Picking a type reveals the rest. Avoids the
        //      "Registration Number on the Teams page" surprise (680.4).
        if (selected.length === 0) {
            if (discriminatorFilterKey) {
                return dynamicAttributeDefs.filter(def => def.typeCodes.length === 0)
            }
            return dynamicAttributeDefs
        }
        return dynamicAttributeDefs.filter(def => def.typeCodes.length === 0
            || def.typeCodes.some(t => selected.includes(t)))
    }, [lockedDiscriminatorValues, discriminatorFilterKey, dynamicAttributeDefs, draft.filters])

    return (
        <div
            ref={containerRef}
            role="region"
            aria-label={title}
            onKeyDown={handleKeyDown}
            className="flex flex-col"
            data-testid="filter-panel"
        >
            {/* Visually hidden heading — provides the region's accessible name. */}
            <h2 className="sr-only" data-testid="filter-panel-title">{title}</h2>

            {/*
             * Responsive grid: 1 column on mobile (<640px), 2 columns on
             * tablet (640–1024px), 3 columns on desktop (≥1024px). Each
             * section is a vertical label+control pair inside a cell;
             * date-range groups span two columns because the from/to pair
             * already uses a two-column inner grid.
             */}
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {singleFilters.map(([key, descriptor]) => (
                    <FilterSection
                        key={key}
                        filterKey={key}
                        descriptor={descriptor}
                        draft={draft}
                        onUpdate={updateFilter}
                        notSetLabel={notSetLabel}
                        anyLabel={anyLabel}
                        yesLabel={yesLabel}
                        noLabel={noLabel}
                        nativeDatePicker={nativeDatePicker}
                        typeValuesByName={typeValuesByName}
                        uuidPlaceholder={uuidPlaceholder}
                        multiPickerInlineThreshold={multiPickerInlineThreshold}
                        filterPickerSearchThreshold={filterPickerSearchThreshold}
                        onValueToggle={onValueToggle}
                    />
                ))}
                {Object.entries(groupedFilters).map(([groupKey, entries]) => (
                    <DateRangeSection
                        key={groupKey}
                        groupKey={groupKey}
                        entries={entries}
                        draft={draft}
                        onUpdate={updateFilter}
                        fromLabel={fromLabel}
                        toLabel={toLabel}
                        nativeDatePicker={nativeDatePicker}
                    />
                ))}
                {scopedDynamicAttributeDefs.map(def => (
                    <DynamicAttributeSection
                        key={def.id}
                        def={def}
                        draft={draft}
                        onUpdate={updateDynamicAttr}
                        notSetLabel={notSetLabel}
                        fromLabel={fromLabel}
                        toLabel={toLabel}
                        multiPickerInlineThreshold={multiPickerInlineThreshold}
                        filterPickerSearchThreshold={filterPickerSearchThreshold}
                        nativeDatePicker={nativeDatePicker}
                    />
                ))}
            </div>

            {/* Sticky footer (issue 342.20) — Apply/Clear stay in the
                viewport while the panel scrolls. The 20+ filter sections on
                the Parties flagship made the buttons hard to find without
                scrolling all the way down. */}
            <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background p-3 z-10">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClearAll}
                    data-testid="btn-filter-clear-all"
                >
                    {clearAllLabel}
                </Button>
                <Button
                    type="button"
                    onClick={handleApply}
                    data-testid="btn-filter-apply"
                >
                    {applyLabel}
                </Button>
            </div>
        </div>
    )
}

// ══════════════════════════════════════════════════════════════════════════
// Per-kind section renderers
// ══════════════════════════════════════════════════════════════════════════

interface SectionProps {
    filterKey: string
    descriptor: EndpointContractFilterDescriptor
    draft: PanelDraft
    onUpdate(key: string, value: string | string[] | undefined): void
    notSetLabel: string
    anyLabel: string
    yesLabel: string
    noLabel: string
    nativeDatePicker?: boolean
    typeValuesByName?: Readonly<Record<string, ReadonlyArray<LocalizedTypeValue>>>
    uuidPlaceholder?: string
    multiPickerInlineThreshold?: number
    filterPickerSearchThreshold?: number
    onValueToggle?(params: {section: string; value: string; selected: boolean}): void
}

function FilterSection({filterKey, descriptor, draft, onUpdate, notSetLabel, anyLabel, yesLabel, noLabel, nativeDatePicker, typeValuesByName, uuidPlaceholder, multiPickerInlineThreshold, filterPickerSearchThreshold, onValueToggle}: SectionProps) {
    const label = requireDisplayName(descriptor)
    const current = draft.filters[filterKey]
    // eslint-disable-next-line i18next/no-literal-string -- technical DOM id
    const legendId = `filter-${filterKey}-legend`

    return (
        <fieldset aria-labelledby={legendId} className="space-y-2" data-testid={`section-${filterKey}`}>
            <legend id={legendId} className="text-sm font-medium">{label}</legend>
            {renderFilterControl({filterKey, descriptor, current, onUpdate, notSetLabel, anyLabel, yesLabel, noLabel, nativeDatePicker, typeValuesByName, uuidPlaceholder, multiPickerInlineThreshold, filterPickerSearchThreshold, onValueToggle})}
        </fieldset>
    )
}

interface RenderArgs {
    filterKey: string
    descriptor: EndpointContractFilterDescriptor
    current: string | string[] | undefined
    onUpdate(key: string, value: string | string[] | undefined): void
    notSetLabel: string
    anyLabel: string
    yesLabel: string
    noLabel: string
    nativeDatePicker?: boolean
    typeValuesByName?: Readonly<Record<string, ReadonlyArray<LocalizedTypeValue>>>
    uuidPlaceholder?: string
    multiPickerInlineThreshold?: number
    filterPickerSearchThreshold?: number
    onValueToggle?(params: {section: string; value: string; selected: boolean}): void
}

/**
 * Resolve a raw enum code (`PERSON`) to its localized label (`Person`, `Dienst`)
 * using the descriptor's {@link EndpointContractFilterDescriptor.typeTable}
 * hint and the caller-provided {@link AppConfig.typeValuesByName}. Falls back
 * to the raw code when no mapping is available — safe for every call site.
 */
function localizedOption(
    descriptor: EndpointContractFilterDescriptor,
    code: string,
    typeValuesByName: Readonly<Record<string, ReadonlyArray<LocalizedTypeValue>>> | undefined,
): string {
    if (!descriptor.typeTable || !typeValuesByName) return code
    const values = typeValuesByName[descriptor.typeTable]
    if (!values) return code
    return labelOf(values as LocalizedTypeValue[], code)
}

function renderFilterControl(args: RenderArgs) {
    const {filterKey, descriptor, current, onUpdate, notSetLabel, anyLabel, yesLabel, noLabel, nativeDatePicker, typeValuesByName, uuidPlaceholder, multiPickerInlineThreshold, filterPickerSearchThreshold, onValueToggle} = args
    const searchThreshold = filterPickerSearchThreshold ?? MULTIPICKER_SEARCH_DEFAULT

    if (descriptor.kind === 'boolean') {
        const val = typeof current === 'string' ? current : ''
        // Per-field labels (preferred) — e.g. Active / Inactive for Party.active.
        // Falls back to the shared Yes / No labels when the descriptor doesn't
        // ship booleanLabels, so every call site renders something sensible.
        const trueLabel = descriptor.booleanLabels?.true ?? yesLabel
        const falseLabel = descriptor.booleanLabels?.false ?? noLabel
        return (
            <div className="flex gap-2 text-sm" role="radiogroup" aria-label={requireDisplayName(descriptor)}>
                <RadioButton
                    testId={`${filterKey}-any`}
                    selected={val === ''}
                    label={anyLabel}
                    onSelect={() => onUpdate(filterKey, '')}
                />
                <RadioButton
                    testId={`${filterKey}-true`}
                    selected={val === BOOLEAN_TRUE}
                    label={trueLabel}
                    onSelect={() => onUpdate(filterKey, BOOLEAN_TRUE)}
                />
                <RadioButton
                    testId={`${filterKey}-false`}
                    selected={val === BOOLEAN_FALSE}
                    label={falseLabel}
                    onSelect={() => onUpdate(filterKey, BOOLEAN_FALSE)}
                />
                {descriptor.nullable && (
                    <RadioButton
                        testId={`${filterKey}-null`}
                        selected={val === IS_NULL_SENTINEL}
                        label={notSetLabel}
                        onSelect={() => onUpdate(filterKey, IS_NULL_SENTINEL)}
                    />
                )}
            </div>
        )
    }

    if (descriptor.kind === 'enum') {
        const val = typeof current === 'string' ? current : ''
        // WHY: typeTable codes MUST come from AppConfig — no fallback to
        // descriptor.values, which is the legacy ENUMSTRICT path and is
        // empty for typeTable-backed filters. Silent fallback hid backend
        // config bugs (issue 539: missing inbound_connector_types) behind
        // empty dropdowns; we fail loud instead (issue 588).
        const optionValues = descriptor.typeTable
            ? requireTypeTableCodes(filterKey, descriptor.typeTable, typeValuesByName)
            : (descriptor.values ?? [])
        return (
            <div className="flex flex-col gap-1.5 text-sm" role="radiogroup" aria-label={requireDisplayName(descriptor)}>
                <RadioButton testId={`${filterKey}-any`} selected={val === ''} label={anyLabel} onSelect={() => onUpdate(filterKey, '')}/>
                {optionValues.map(v => (
                    <RadioButton
                        key={v}
                        testId={`filter-option-${filterKey}-${v}`}
                        selected={val === v}
                        label={localizedOption(descriptor, v, typeValuesByName)}
                        onSelect={() => onUpdate(filterKey, v)}
                    />
                ))}
                {descriptor.nullable && (
                    <RadioButton
                        testId={`${filterKey}-null`}
                        selected={val === IS_NULL_SENTINEL}
                        label={notSetLabel}
                        onSelect={() => onUpdate(filterKey, IS_NULL_SENTINEL)}
                    />
                )}
            </div>
        )
    }

    if (descriptor.kind === 'csv') {
        const arr = Array.isArray(current) ? current : (current ? (current as string).split(',') : [])
        // WHY: multi-select entity-reference picker — `csv` of UUIDs with a `lookup`
        // descriptor (issue 429 Part B). Renders the chip-list above the
        // generic `LookupCombobox`; pick/remove updates the draft, Apply
        // commits the CSV to the URL (`?roleIds=u1,u2`).
        if (descriptor.element === 'uuid' && descriptor.lookup) {
            return (
                <LookupCombobox
                    mode="multi"
                    values={arr.map((packed) => toEntityLink(packed, descriptor))}
                    onChange={(next) => onUpdate(filterKey, next.map(fromEntityLink))}
                    lookup={descriptor.lookup}
                    displayName={requireDisplayName(descriptor)}
                    testIdPrefix={filterKey}
                />
            )
        }
        // Chip picker for ENUM-style multi-select. Two source-of-values paths
        // share the same UI per spec §4.7 / project ENUM convention:
        //   1. ENUMSTRICT (Java enum) — descriptor.values is pre-populated
        //      by the backend extractor at startup.
        //   2. ENUM (type-table-backed, e.g. PartyTypeKey → party_types) —
        //      values come from AppConfig at render time via descriptor.typeTable.
        // typeTable wins when set: codes MUST come from AppConfig, no silent
        // fallback to descriptor.values (issue 588).
        const optionValues = descriptor.element === 'enum' && descriptor.typeTable
            ? requireTypeTableCodes(filterKey, descriptor.typeTable, typeValuesByName)
            : descriptor.values
        if (descriptor.element === 'enum' && optionValues) {
            const inlineCap = multiPickerInlineThreshold ?? MULTIPICKER_INLINE_DEFAULT
            // Issues 342.26 + 342.27: every ENUM filter renders as the chip
            // Combobox by default (universal-Combobox per user feedback —
            // beats the old vertical checkbox list for panel-space economy).
            // ONE exception: tiny option sets (≤ inlineCap) with short
            // enough labels render as an INLINE HORIZONTAL TOGGLE ROW so a
            // 2- or 3-button choice doesn't cost the user a popover click.
            const labels = optionValues.map(code => localizedOption(descriptor, code, typeValuesByName))
            const fitsInline = optionValues.length <= inlineCap
                    && labels.every(l => l.length <= MULTIPICKER_INLINE_MAX_LABEL_LENGTH)
            if (!fitsInline) {
                const popoverOptions = optionValues.map((code, i) => ({code, label: labels[i]}))
                const showSearch = optionValues.length > searchThreshold
                return (
                    <MultiPickerPopover
                        pickerKey={filterKey}
                        label={requireDisplayName(descriptor)}
                        options={popoverOptions}
                        selected={arr}
                        showSearch={showSearch}
                        onChange={(next) => onUpdate(filterKey, next)}
                    />
                )
            }
            return (
                <div className="flex flex-row flex-wrap gap-2 text-sm" data-testid={`inline-multipicker-${filterKey}`}>
                    {optionValues.map((v, i) => {
                        const checked = arr.includes(v)
                        return (
                            <button
                                key={v}
                                type="button"
                                aria-pressed={checked}
                                data-testid={`filter-option-${filterKey}-${v}`}
                                onClick={() => {
                                    const next = checked
                                            ? arr.filter(x => x !== v)
                                            : [...arr.filter(x => x !== v), v]
                                    onUpdate(filterKey, next)
                                    onValueToggle?.({
                                        section: requireDisplayName(descriptor),
                                        value: labels[i],
                                        selected: !checked,
                                    })
                                }}
                                className={checked
                                        ? 'border border-primary bg-primary text-primary-foreground rounded-full px-3 py-1'
                                        : 'border border-border hover:bg-muted rounded-full px-3 py-1'}
                            >
                                {labels[i]}
                            </button>
                        )
                    })}
                </div>
            )
        }
        // CSV of strings — free-form tag input (simple textarea, comma-separated).
        return (
            <Input
                type="text"
                value={arr.join(', ')}
                data-testid={`input-${filterKey}`}
                onChange={(e) => {
                    const raw = e.target.value
                    const parts = raw.split(',').map(s => s.trim()).filter(s => s.length > 0)
                    onUpdate(filterKey, parts)
                }}
                placeholder={PLACEHOLDER_TAG_INPUT}
                aria-label={requireDisplayName(descriptor)}
            />
        )
    }

    if (descriptor.kind === 'uuid') {
        const val = typeof current === 'string' ? current : ''
        // WHY: single-select entity-reference picker — `uuid` with a `lookup`
        // descriptor (issue 429 Part B). Renders the generic `LookupCombobox`;
        // pick replaces the value, clear unsets it. Apply commits to the
        // URL as `?roleId=<uuid>`.
        if (descriptor.lookup && val !== IS_NULL_SENTINEL) {
            return (
                <div className="space-y-2">
                    {descriptor.nullable && (
                        <label className="inline-flex items-center gap-2 text-sm">
                            <Checkbox
                                checked={false}
                                onCheckedChange={(c) => onUpdate(filterKey, c ? IS_NULL_SENTINEL : '')}
                                data-testid={`${filterKey}-null`}
                            />
                            <span>{notSetLabel}</span>
                        </label>
                    )}
                    <LookupCombobox
                        mode="single"
                        value={val.length > 0 ? toEntityLink(val, descriptor) : null}
                        onChange={(next) => onUpdate(filterKey, next ? fromEntityLink(next) : '')}
                        lookup={descriptor.lookup}
                        displayName={requireDisplayName(descriptor)}
                        testIdPrefix={filterKey}
                    />
                </div>
            )
        }
        // UUID filter renders as a plain UUID text input until the entity-picker
        // combobox is wired per-page in the B-phase migrations (issue 342.04
        // Parties is the first pilot). This is the documented SEC-06 graceful
        // fallback — the placeholder tells the user the picker is pending so
        // the state doesn't read as "finished UI that demands UUIDs."
        return (
            <div className="space-y-2">
                {descriptor.nullable && (
                    <label className="inline-flex items-center gap-2 text-sm">
                        <Checkbox
                            checked={val === IS_NULL_SENTINEL}
                            onCheckedChange={(c) => onUpdate(filterKey, c ? IS_NULL_SENTINEL : '')}
                            data-testid={`${filterKey}-null`}
                        />
                        <span>{notSetLabel}</span>
                    </label>
                )}
                <Input
                    type="text"
                    value={val === IS_NULL_SENTINEL ? '' : val}
                    disabled={val === IS_NULL_SENTINEL}
                    data-testid={`input-${filterKey}`}
                    onChange={(e) => onUpdate(filterKey, e.target.value)}
                    placeholder={uuidPlaceholder ?? PLACEHOLDER_UUID}
                    aria-label={requireDisplayName(descriptor)}
                />
            </div>
        )
    }

    if (descriptor.kind === 'date' || descriptor.kind === 'datetime') {
        const val = typeof current === 'string' ? current : ''
        const isDateTime = descriptor.kind === 'datetime'
        return (
            <div className="space-y-2">
                {descriptor.nullable && (
                    <label className="inline-flex items-center gap-2 text-sm">
                        <Checkbox
                            checked={val === IS_NULL_SENTINEL}
                            onCheckedChange={(c) => onUpdate(filterKey, c ? IS_NULL_SENTINEL : '')}
                            data-testid={`${filterKey}-null`}
                        />
                        <span>{notSetLabel}</span>
                    </label>
                )}
                <DatePicker
                    testId={`input-${filterKey}`}
                    value={val === IS_NULL_SENTINEL ? '' : val}
                    disabled={val === IS_NULL_SENTINEL}
                    withTime={isDateTime}
                    forceNative={nativeDatePicker}
                    ariaLabel={requireDisplayName(descriptor)}
                    onChange={(v) => onUpdate(filterKey, v)}
                />
            </div>
        )
    }

    // STRING fallback.
    const val = typeof current === 'string' ? current : ''
    return (
        <Input
            type="text"
            value={val}
            data-testid={`input-${filterKey}`}
            onChange={(e) => onUpdate(filterKey, e.target.value)}
            aria-label={requireDisplayName(descriptor)}
        />
    )
}

// ══════════════════════════════════════════════════════════════════════════
// Shared radio + date-range + dynamic-attribute helpers
// ══════════════════════════════════════════════════════════════════════════

function RadioButton({testId, selected, label, onSelect}: {testId: string; selected: boolean; label: string; onSelect: () => void}) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={testId}
            onClick={onSelect}
            className={cn(
                'inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-left',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selected ? 'border-primary bg-primary/10' : 'border-input hover:bg-accent',
            )}
        >
            <span
                aria-hidden="true"
                className={cn(
                    'inline-block h-3 w-3 rounded-full border',
                    selected ? 'border-primary bg-primary' : 'border-muted-foreground',
                )}
            />
            {label}
        </button>
    )
}

interface DateRangeSectionProps {
    groupKey: string
    entries: Array<[string, EndpointContractFilterDescriptor]>
    draft: PanelDraft
    onUpdate(key: string, value: string | undefined): void
    fromLabel: string
    toLabel: string
    nativeDatePicker?: boolean
}

function DateRangeSection({groupKey, entries, draft, onUpdate, fromLabel, toLabel, nativeDatePicker}: DateRangeSectionProps) {
    const fromEntry = entries.find(([, d]) => d.dateRangeBound === 'from')
    const toEntry = entries.find(([, d]) => d.dateRangeBound === 'to')
    // eslint-disable-next-line i18next/no-literal-string -- technical DOM id
    const legendId = `daterange-${groupKey}-legend`
    // Group label — use "from" descriptor's displayName minus any trailing
    // "from" word. Both sides of a date-range pair must declare displayName
    // (contract invariant), so we resolve through the strict helper.
    const sourceDescriptor = fromEntry?.[1] ?? toEntry?.[1]
    if (!sourceDescriptor) {
        throw new Error(
            `Date-range group '${groupKey}' has no descriptors — a grouped filter must declare at least one from/to side.`,
        )
    }
    const label = requireDisplayName(sourceDescriptor)
        .replace(/\s+(from|since|after)\s*$/i, '')
        .trim()
    // The pair must declare a uniform kind. We pick from the first available
    // descriptor; spec amendment §4.7 says both bounds share their kind.
    const isDateTime = sourceDescriptor.kind === 'datetime'

    return (
        <fieldset aria-labelledby={legendId} className="space-y-2 md:col-span-2" data-testid={`section-daterange-${groupKey}`}>
            <legend id={legendId} className="text-sm font-medium">{label}</legend>
            <div className="grid grid-cols-2 gap-2">
                {fromEntry && (
                    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                        <span>{fromLabel}</span>
                        <DatePicker
                            testId={`input-daterange-${groupKey}-from`}
                            value={(draft.filters[fromEntry[0]] as string) ?? ''}
                            withTime={isDateTime}
                            forceNative={nativeDatePicker}
                            ariaLabel={`${label} ${fromLabel}`}
                            onChange={(v) => onUpdate(fromEntry[0], v)}
                        />
                    </label>
                )}
                {toEntry && (
                    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                        <span>{toLabel}</span>
                        <DatePicker
                            testId={`input-daterange-${groupKey}-to`}
                            value={(draft.filters[toEntry[0]] as string) ?? ''}
                            withTime={isDateTime}
                            forceNative={nativeDatePicker}
                            ariaLabel={`${label} ${toLabel}`}
                            onChange={(v) => onUpdate(toEntry[0], v)}
                        />
                    </label>
                )}
            </div>
        </fieldset>
    )
}

interface DynamicAttributeSectionProps {
    def: AttributeDefinition
    draft: PanelDraft
    onUpdate(attrName: string, value: string | string[] | undefined): void
    notSetLabel: string
    fromLabel: string
    toLabel: string
    multiPickerInlineThreshold?: number
    filterPickerSearchThreshold?: number
    nativeDatePicker?: boolean
}

// Issue 342.24 — URL suffix that marks the lower / upper bound of a range
// filter. Mirrors the backend resolver constants.
const RANGE_FROM_SUFFIX = 'From'
const RANGE_TO_SUFFIX = 'To'

function DynamicAttributeSection({def, draft, onUpdate, notSetLabel, fromLabel, toLabel, multiPickerInlineThreshold, filterPickerSearchThreshold, nativeDatePicker}: DynamicAttributeSectionProps) {
    const searchThreshold = filterPickerSearchThreshold ?? MULTIPICKER_SEARCH_DEFAULT
    const current = draft.dynamicAttributes[def.name]
    // eslint-disable-next-line i18next/no-literal-string -- technical DOM id
    const legendId = `attr-${def.id}-legend`
    const allowedValues = def.resolvedAllowedValues ?? []

    let control: React.ReactNode
    // Issue 342.24 — DATE / TIMESTAMP / NUMBER attributes render as a
    // from/to range pair. Each bound stores its value under the
    // `<name>From` / `<name>To` key so the existing dynamicAttributes URL
    // serializer emits `attr.<name>From` / `attr.<name>To` as-is.
    if (allowedValues.length === 0
            && (def.valueType === 'DATE' || def.valueType === 'TIMESTAMP' || def.valueType === 'NUMBER')) {
        const fromKey = `${def.name}${RANGE_FROM_SUFFIX}`
        const toKey = `${def.name}${RANGE_TO_SUFFIX}`
        const fromVal = typeof draft.dynamicAttributes[fromKey] === 'string'
                ? (draft.dynamicAttributes[fromKey] as string) : ''
        const toVal = typeof draft.dynamicAttributes[toKey] === 'string'
                ? (draft.dynamicAttributes[toKey] as string) : ''
        control = (
            <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    <span>{fromLabel}</span>
                    {renderRangeInput(def, fromKey, fromVal, def.displayName, fromLabel, nativeDatePicker, onUpdate)}
                </label>
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    <span>{toLabel}</span>
                    {renderRangeInput(def, toKey, toVal, def.displayName, toLabel, nativeDatePicker, onUpdate)}
                </label>
            </div>
        )
    } else if (allowedValues.length > 0) {
        const arr = Array.isArray(current) ? current : (current ? (current as string).split(',') : [])
        const inlineCap = multiPickerInlineThreshold ?? MULTIPICKER_INLINE_DEFAULT
        // Same universal-Combobox + inline-row policy as the static-ENUM
        // branch above (issues 342.26 + 342.27). Dynamic-attribute path
        // additionally carries a "Not Set" sentinel — counted as one option
        // for the inline-fit check so the row always includes it.
        // eslint-disable-next-line i18next/no-literal-string -- technical DOM ids
        const pickerKey = `attr-${def.name}`
        const fitsInline = (allowedValues.length + 1) <= inlineCap
                && allowedValues.every(v => v.label.length <= MULTIPICKER_INLINE_MAX_LABEL_LENGTH)
                && notSetLabel.length <= MULTIPICKER_INLINE_MAX_LABEL_LENGTH
        if (!fitsInline) {
            const showSearch = allowedValues.length > searchThreshold
            control = (
                <MultiPickerPopover
                    pickerKey={pickerKey}
                    label={def.displayName}
                    options={allowedValues.map(v => ({code: v.code, label: v.label}))}
                    selected={arr}
                    optionTestIdPrefix={pickerKey}
                    showSearch={showSearch}
                    onChange={(next) => onUpdate(def.name, next)}
                />
            )
        } else {
            control = (
                <div className="flex flex-row flex-wrap gap-2 text-sm" data-testid={`inline-multipicker-${pickerKey}`}>
                    {allowedValues.map(v => {
                        const checked = arr.includes(v.code)
                        return (
                            <button
                                key={v.code}
                                type="button"
                                aria-pressed={checked}
                                data-testid={`attr-${def.name}-${v.code}`}
                                onClick={() => {
                                    const next = checked
                                            ? arr.filter(x => x !== v.code)
                                            : [...arr.filter(x => x !== v.code), v.code]
                                    onUpdate(def.name, next)
                                }}
                                className={checked
                                        ? 'border border-primary bg-primary text-primary-foreground rounded-full px-3 py-1'
                                        : 'border border-border hover:bg-muted rounded-full px-3 py-1'}
                            >
                                {v.label}
                            </button>
                        )
                    })}
                    <button
                        type="button"
                        aria-pressed={arr.includes(IS_NULL_SENTINEL)}
                        data-testid={`attr-${def.name}-null`}
                        onClick={() => {
                            const checked = arr.includes(IS_NULL_SENTINEL)
                            const next = checked ? arr.filter(x => x !== IS_NULL_SENTINEL) : [IS_NULL_SENTINEL]
                            onUpdate(def.name, next)
                        }}
                        className={arr.includes(IS_NULL_SENTINEL)
                                ? 'border border-primary bg-primary text-primary-foreground rounded-full px-3 py-1'
                                : 'border border-border hover:bg-muted rounded-full px-3 py-1'}
                    >
                        {notSetLabel}
                    </button>
                </div>
            )
        }
    } else {
        const val = typeof current === 'string' ? current : ''
        control = (
            <Input
                type="text"
                value={val}
                data-testid={`input-attr-${def.name}`}
                onChange={(e) => onUpdate(def.name, e.target.value)}
                aria-label={def.displayName}
            />
        )
    }

    return (
        <fieldset aria-labelledby={legendId} className="space-y-2" data-testid={`section-attr-${def.name}`}>
            <legend id={legendId} className="text-sm font-medium">{def.displayName}</legend>
            {control}
        </fieldset>
    )
}

/**
 * Issue 342.24 — render the bound input for a DATE / TIMESTAMP / NUMBER
 * dynamic-attribute range filter. NUMBER renders as a typed numeric input
 * (single value per bound); DATE and TIMESTAMP both go through
 * {@link DatePicker} (TIMESTAMP sets {@code withTime}).
 */
function renderRangeInput(
    def: AttributeDefinition,
    paramKey: string,
    value: string,
    boundDisplayName: string,
    boundLabel: string,
    nativeDatePicker: boolean | undefined,
    onUpdate: (attrName: string, value: string | string[] | undefined) => void,
): React.ReactNode {
    const ariaLabel = `${boundDisplayName} ${boundLabel}`
    if (def.valueType === 'TIMESTAMP') {
        return (
            <DatePicker
                testId={`input-attr-${paramKey}`}
                value={value}
                withTime={true}
                forceNative={nativeDatePicker}
                ariaLabel={ariaLabel}
                onChange={(v) => onUpdate(paramKey, v)}
            />
        )
    }
    if (def.valueType === 'DATE') {
        return (
            <DatePicker
                testId={`input-attr-${paramKey}`}
                value={value}
                forceNative={nativeDatePicker}
                ariaLabel={ariaLabel}
                onChange={(v) => onUpdate(paramKey, v)}
            />
        )
    }
    return (
        <Input
            type="number"
            value={value}
            data-testid={`input-attr-${paramKey}`}
            onChange={(e) => onUpdate(paramKey, e.target.value)}
            aria-label={ariaLabel}
        />
    )
}

/**
 * Resolve the {@link EndpointContractFilterDescriptor.displayName} — throwing
 * when absent. The contract type requires `displayName`, so a missing value at
 * runtime indicates a contract-codegen bug or a hand-rolled descriptor that
 * skipped the field. We fail fast with a clear error rather than fabricating a
 * label from the param name — HQG round: "no silent fallbacks — break if
 * something is wrong".
 */
function requireDisplayName(descriptor: EndpointContractFilterDescriptor): string {
    if (!descriptor.displayName) {
        throw new Error(
            `Filter descriptor '${descriptor.name}' missing displayName — contract must declare one`,
        )
    }
    return descriptor.displayName
}

/**
 * Resolve the codes for a typeTable-backed enum filter. When a contract
 * declares `typeTable: 'X'`, the codes MUST come from
 * `AppConfig.typeValuesByName[X]` — falling through to `descriptor.values`
 * (which is empty for typeTable-backed filters) would silently render zero
 * options, hiding a backend config bug behind a useless dropdown. Fail loud
 * instead so the gap (typeTable not registered in TypeService.TYPE_TABLES /
 * STATIC_ENUMS, or the response payload was malformed) is visible immediately.
 */
function requireTypeTableCodes(
    filterKey: string,
    typeTable: string,
    typeValuesByName: Readonly<Record<string, ReadonlyArray<LocalizedTypeValue>>> | undefined,
): ReadonlyArray<string> {
    const entries = typeValuesByName?.[typeTable]
    if (!entries || entries.length === 0) {
        throw new Error(
            `Filter '${filterKey}' references typeTable '${typeTable}', but AppConfig does not expose it. `
            + `Register the table in TypeService.TYPE_TABLES or TypeService.STATIC_ENUMS, `
            + `or correct the contract.`,
        )
    }
    return entries.map(v => v.code)
}

// DatePickerControl has been replaced by the shared {@link DatePicker}
// component at {@file src/components/shared/DatePicker.tsx} so every page,
// form, dialog, and filter panel renders the same calendar-in-popover
// surface. The migration for the remaining ~15 raw `<input type="date">`
// call-sites is tracked in issue 355 — new code must NOT hand-roll a date
// input.

interface MultiPickerOption {
    code: string
    label: string
}

interface MultiPickerPopoverProps {
    /** Stable identifier used for `data-testid` prefixes — must be unique per panel. */
    pickerKey: string
    /** Accessible label for the trigger; rendered as fallback when nothing is selected. */
    label: string
    /** Available options as `{code, label}` pairs. */
    options: ReadonlyArray<MultiPickerOption>
    /** Currently-selected codes (strings — labels are looked up from `options`). */
    selected: ReadonlyArray<string>
    /** Per-option `data-testid` prefix — defaults to `filter-option-${pickerKey}` (legacy). */
    optionTestIdPrefix?: string
    /**
     * When true, render the inner search input above the option list. When
     * false (or omitted), the search input is omitted — the user scans the
     * list visually. Caller decides based on the option count vs the
     * `filterPickerSearchThreshold` from AppConfig. Issue 401.
     */
    showSearch?: boolean
    onChange(next: string[]): void
}

/**
 * Popover-anchored multi-select for ENUM-style filters whose value count
 * exceeds the inline threshold. Trigger button shows selected labels (or the
 * field label when nothing is selected); popover hosts an option list with
 * a checkbox per entry. Whether an inner search input appears above the list
 * is gated by the `showSearch` prop — the caller compares the option count
 * against `AppConfig.filterPickerSearchThreshold` to decide.
 *
 * Issue 342.15: collapses long checkbox lists to a single row in the panel.
 * Issue 401: search input gated by a separate threshold so moderate lists
 * (4–10 options) render without clutter.
 *
 * Used by both the static csv-enum branch and the dynamic-attribute branch.
 */
function MultiPickerPopover({pickerKey, label, options, selected, optionTestIdPrefix, showSearch, onChange}: MultiPickerPopoverProps) {
    const [search, setSearch] = useState('')
    const filtered = useMemo(() => {
        if (!showSearch || !search) return options
        const needle = search.toLowerCase()
        return options.filter(opt =>
            opt.code.toLowerCase().includes(needle) || opt.label.toLowerCase().includes(needle))
    }, [options, search, showSearch])

    const triggerLabel = selected.length === 0
        ? label
        : options.filter(o => selected.includes(o.code)).map(o => o.label).join(', ')
    // eslint-disable-next-line i18next/no-literal-string -- technical DOM id
    const testIdPrefix = optionTestIdPrefix ?? `filter-option-${pickerKey}`

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between text-left font-normal"
                    data-testid={`multipicker-${pickerKey}`}
                    aria-label={label}
                >
                    <span className="truncate">{triggerLabel}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{selected.length || ''}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-2" align="start">
                {showSearch && (
                    <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label={label}
                        data-testid={`multipicker-search-${pickerKey}`}
                        className="mb-2"
                    />
                )}
                <div className="flex max-h-64 flex-col gap-1.5 overflow-y-auto text-sm">
                    {filtered.map(opt => {
                        const checked = selected.includes(opt.code)
                        return (
                            <label key={opt.code} className="inline-flex items-center gap-2">
                                <Checkbox
                                    checked={checked}
                                    data-testid={`${testIdPrefix}-${opt.code}`}
                                    onCheckedChange={(c) => {
                                        const next = c
                                            ? [...selected.filter(x => x !== opt.code), opt.code]
                                            : selected.filter(x => x !== opt.code)
                                        onChange(next)
                                    }}
                                />
                                <span>{opt.label}</span>
                            </label>
                        )
                    })}
                </div>
            </PopoverContent>
        </Popover>
    )
}
